import React, {useEffect, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {Badge, Button, Checkbox, Dropdown, Grid, Input, Menu, Message, Modal} from "@arco-design/web-react";
import {IconPlus, IconDelete, IconRefresh, IconDownload, IconTag, IconSave, IconList, IconMinusCircle, IconStop, IconFilter} from "@arco-design/web-react/icon";
import Content from "../Layout/Content";
import ContactAddEditDialog from "./ContactAddEditDialog";
import DeleteContactDialog from "./DeleteContactDialog";

import {useAuth0} from '@auth0/auth0-react';
import {DeleteApi, DownloadApi, FetchApi, PostApi, SaveApi} from '../Api/AuthenticatedApi';
import ContactImportDialog from "./ContactImportDialog";

import "react-csv-importer/dist/index.css";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import SummaryCard from "../Layout/SummaryCard";
import FilterDrawer from "../Components/ContactFilter/FilterDrawer";
import { FilterChips } from "../Components/ContactFilter/FilterBuilder";
import useFilterOptions from "../Components/ContactFilter/useFilterOptions";
import { filterFromParam, filterToParam, forServer, fromLegacy, removeTerm, termCount } from "../Components/ContactFilter/contactFilter";
import InfiniteScrollContactTable from "./ContactTable";
import ColumnPicker from "./ColumnPicker";
import { DEFAULT_SORT, loadVisibleColumns, saveVisibleColumns, visibleColumns } from "./contactColumns";
import MailTextFilter from "./MailTextFilter";
import UseLoadContacts from "./UseLoadContacts";

const { Row, Col } = Grid;

// MUI -> Arco. The makeStyles classes here were entirely unused (only the
// no-longer-present root div referenced one), so they're dropped wholesale. Button
// variants map (contained -> primary, outlined -> outline); AddIcon -> IconPlus,
// DeleteIcon -> IconDelete, Refresh -> IconRefresh. FormControlLabel + Checkbox
// -> Arco Checkbox with a child label (its onChange just toggles, so no value is
// read). Grid -> Arco Grid Row/Col with MUI /12 spans doubled to /24.

// Filename-safe slug: lowercase, non-alphanumerics -> single hyphen, trimmed and
// length-capped so a long search term can't blow out the filename.
const slugify = (value) =>
    String(value).toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

export default function Contacts() {
    // Which columns the table shows (remembered per browser) and the contact
    // custom field definitions that can be shown as columns.
    const [visibleColumnKeys, setVisibleColumnKeysState] = useState(loadVisibleColumns);
    const [customFieldDefs, setCustomFieldDefs] = useState([]);
    const setVisibleColumnKeys = (keys) => {
        setVisibleColumnKeysState(keys);
        saveVisibleColumns(keys);
    };
    const [selected, setSelected] = useState([]);
    const [selectAllLocal, setSelectAllLocal] = useState(false);
    const [selectedAllModeCommitted, setSelectedAllModeCommitted] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [isAddEditOpen, setIsAddEditOpen] = useState(false);

    let history = useNavigate();
    const { getAccessTokenSilently } = useAuth0();

    const [allMailListItems, setAllMailListItems] = useState([]);
    const [isDataUpdated, setIsDataUpdated] = useState(false);

    // these are for the selected row when editing... is there away around doing this?
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [contactId, setContactId] = useState("");
    const [subscriptions, setSubscriptions] = useState([]);
    const [contactCompanyId, setContactCompanyId] = useState("");
    const [contactCustomFields, setContactCustomFields] = useState({});
    const [contactRecordTypeId, setContactRecordTypeId] = useState("");
    const [autoOpened, setAutoOpened] = useState(false);


    // -------------------------
    // Contact scrolling stuff
    // -------------------------
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    // The filter comes from ?filter= (JSON), or from the older list / status /
    // tag params that existing links still use.
    const [initialFilter] = useState(() =>
        filterFromParam(queryParams.get('filter')) ||
        fromLegacy({ listId: queryParams.get('initialContactListFilterId'), status: queryParams.get('status'), tagId: queryParams.get('tag') }));
    const initialSearch = queryParams.get('searchTerm');
    const openEmail = queryParams.get('open');
    const { totalCount, contactListFilterId, filter, setFilter, searchTerm, setSearchTerm, sort, setSort, setShouldUpdateDataFromServer,
        loadItemsLoading, contactItems, hasNextPage, error: loadContactsError, loadMore } = UseLoadContacts(initialFilter, initialSearch);
    const filterOptions = useFilterOptions();
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // The table's filters as the server takes them (bulk actions, delete,
    // counts): the same ones the rows were loaded with, so counts agree.
    const matchingPayload = () => ({
        search_term: searchTerm || "",
        contact_list_filter_id: contactListFilterId,
        filter: forServer(filter),
    });

    // Why the list is empty decides what it says: only a list that's really
    // empty offers to add the first contact.
    const filtering = !!searchTerm || !!filterToParam(forServer(filter));
    const stillLoading = loadItemsLoading || (hasNextPage && !loadContactsError);
    const emptyState = stillLoading ? "loading" : (filtering ? "filtered" : "none");
    const noContactsYet = contactItems.length === 0 && emptyState === "none" && !loadContactsError;

    // Keep the URL in step, so a filtered view can be bookmarked or shared.
    const writeUrl = (nextFilter, nextSearch) => {
        const params = new URLSearchParams();
        const f = filterToParam(forServer(nextFilter));
        if (f) params.set("filter", f);
        if (nextSearch) params.set("searchTerm", nextSearch);
        const qs = params.toString();
        history("/contacts" + (qs ? "?" + qs : ""), { replace: true });
    };

    // A "select all on server" tick is for the filters it was made under;
    // changing the filter or search clears it, so an action never uses
    // another filter's tick.
    const applyFilter = (next) => {
        setSelectedAllModeCommitted(false);
        setFilter(next);
        setShouldUpdateDataFromServer([{ operation: "refresh" }]);
        writeUrl(next, searchTerm);
    };

    const [tags, setTags] = useState([]);
    const [newTagModalVisible, setNewTagModalVisible] = useState(false);
    const [newTagName, setNewTagName] = useState("");
    const [segmentModalVisible, setSegmentModalVisible] = useState(false);
    const [segmentName, setSegmentName] = useState("");

    const handleSaveSegment = () => {
        const name = segmentName.trim();
        if (!name) return;
        PostApi(getAccessTokenSilently, "/api/segments/",
            () => {
                setSegmentModalVisible(false);
                setSegmentName("");
                Message.success("Segment saved");
            },
            () => history("/login"),
            () => Message.error("Failed to save segment"),
            { name, search_term: searchTerm || "", filter: forServer(filter) },
        );
    };

    const handleCreateAndTag = () => {
        const name = newTagName.trim();
        if (!name) return;
        PostApi(getAccessTokenSilently, "/api/tags/",
            (data) => {
                setNewTagModalVisible(false);
                setNewTagName("");
                if (data) {
                    setTags((prev) => [...prev, data]);
                    if (data.id) handleBulkTag(data.id);
                }
            },
            () => history("/login"),
            () => Message.error("Failed to create tag"),
            { name },
        );
    };

    // Sorting is server-side (rows page in as you scroll), so a new order reloads.
    const handleSortChange = (value) => {
        setSort(value || DEFAULT_SORT);
        setShouldUpdateDataFromServer([{ operation: "refresh" }]);
    };

    const handleRemoveTagFromContact = (contactId, tagId) => {
        PostApi(getAccessTokenSilently, "/api/tags/" + tagId + "/remove",
            () => {
                Message.success("Tag removed");
                setShouldUpdateDataFromServer([{ operation: "refresh" }]);
            },
            () => history("/login"),
            () => Message.error("Failed to remove tag"),
            { contact_ids: [contactId] },
        );
    };

    // How many contacts a bulk action would change: every match on the server
    // when "Select all on server" is ticked, otherwise the ticked rows.
    const bulkCount = selectedAllModeCommitted ? totalCount : selected.length;

    // runBulk applies one action to the ticked rows, or, with "Select all on
    // server" ticked, to every contact matching the filters. The server refuses
    // a match with no filter, or if the count is no longer the one shown.
    const runBulk = (action, extra, doneText) => {
        const target = selectedAllModeCommitted
            ? { ...matchingPayload(), expected_count: totalCount }
            : { contact_ids: selected };
        PostApi(getAccessTokenSilently, "/api/contacts/bulk",
            (data) => {
                Message.success(doneText((data && data.count) || 0));
                setSelected([]);
                setSelectedAllModeCommitted(false);
                setSelectAllLocal(false);
                setIsDataUpdated((prev) => !prev); // list counts change too
                setShouldUpdateDataFromServer([{ operation: "refresh" }]);
            },
            () => history("/login"),
            (err) => Message.error((err && err.message) || "Failed to update contacts"),
            { action, ...extra, ...target },
        );
    };

    const handleBulkTag = (tagId) =>
        runBulk("tag", { tag_id: tagId }, (n) => "Tagged " + n + " contact(s)");

    const listName = (listId) => (allMailListItems.find((l) => l.id === listId) || {}).name || "the list";

    const handleBulkAddToList = (listId) =>
        runBulk("add_to_list", { list_id: listId }, (n) => "Added " + n + " contact(s) to " + listName(listId));

    const handleBulkRemoveFromList = (listId) =>
        runBulk("remove_from_list", { list_id: listId }, (n) => "Removed " + n + " contact(s) from " + listName(listId));

    // Unsubscribing is hard to undo (they'd have to sign up again), so ask first.
    const handleBulkUnsubscribe = () => {
        Modal.confirm({
            title: "Unsubscribe " + bulkCount + " contact(s)?",
            content: "They won't get any more email from you, on any list. Contacts that bounced or complained keep that status.",
            okText: "Unsubscribe",
            okButtonProps: { status: "danger" },
            onOk: () => runBulk("unsubscribe", {}, (n) => "Unsubscribed " + n + " contact(s)"),
        });
    };


    const handleRefreshList = () => {
        setShouldUpdateDataFromServer((contactItems) => [...contactItems, { operation: "refresh", updated_info: { source: "button press" } }]);
    }

    const handleExport = () => {
        const params = new URLSearchParams();
        if (searchTerm) params.set("searchTerm", searchTerm);
        if (contactListFilterId) params.set("contactListFilterId", contactListFilterId);
        const f = filterToParam(forServer(filter));
        if (f) params.set("filter", f);
        if (sort) params.set("sort", sort); // export in the order the table shows
        const qs = params.toString();
        DownloadApi(
            getAccessTokenSilently,
            "/api/contacts/export" + (qs ? "?" + qs : ""),
            exportFilename(),
            () => Message.error("Failed to export contacts"),
        );
    }

    // Name the download after what's in it, so exports don't all collide as
    // "contacts.csv": the list every row is on, "filtered" for other rules,
    // the search text, and the date.
    const exportFilename = () => {
        const parts = ["contacts"];
        if (contactListFilterId && contactListFilterId !== "all") {
            const list = allMailListItems.find((l) => l.id === contactListFilterId);
            if (list && list.name) parts.push(slugify(list.name));
        }
        if (termCount(forServer(filter)) > (contactListFilterId !== "all" ? 1 : 0)) parts.push("filtered");
        if (searchTerm) parts.push(slugify(searchTerm));
        parts.push(new Date().toISOString().slice(0, 10));
        return parts.filter(Boolean).join("-") + ".csv";
    }


    const handleSearchFilterChange = (value) => {
        if (value !== searchTerm) {
            setSelectedAllModeCommitted(false);
            setSearchTerm(value);
            setShouldUpdateDataFromServer([{ operation: "refresh" }]);
            writeUrl(filter, value);
        }
    }

    const handleContactSaved = (contactRecord) => {
        SaveApi(getAccessTokenSilently,
            "/api/contacts/" + contactRecord.id,
            (updatedContact) => {
                setIsDataUpdated((prevIsDataUpdated) => !prevIsDataUpdated);

                // When a filter is active, the edit may have changed whether this
                // contact still matches it (e.g. removing them from the very list
                // we're filtering on), so re-query the server to drop/add the row.
                // With no filter active, patch the row in place to keep scroll pos.
                const filterActive = termCount(forServer(filter)) > 0 || !!searchTerm;
                if (filterActive) {
                    setShouldUpdateDataFromServer([{ operation: "refresh" }]);
                } else {
                    setShouldUpdateDataFromServer([{ operation: "update", updated_record: updatedContact }]);
                }
                Message.success("Contact saved");
            },
            (response) => {
                history("/login");
            }, (response) => {
                Message.error("Failed to save contact");
            }, contactRecord)
    }

    const handleContactsAdded = (contactRecords) => {
        PostApi(getAccessTokenSilently,
            "/api/contacts/",
            (data) => {
                setIsDataUpdated((prevIsDataUpdated) => !prevIsDataUpdated);
                // Reload from the server so the new contact(s) show up immediately,
                // matching how delete refreshes — instead of just nudging the user
                // with a Refresh(N) counter.
                setShouldUpdateDataFromServer((contactItems) => [...contactItems, { operation: "refresh", updated_info: data }]);
                Message.success("Contact added");
            },
            (response) => {
                history("/login");
            }, (response) => {
                Message.error("Failed to add contact");
            }, contactRecords)
    }

    const handleRemoveContactsById = (contactIds) => {
        setSelectedAllModeCommitted(false);
        setSelectAllLocal(false);

        DeleteApi(getAccessTokenSilently,
            "/api/contacts/",
            (data) => {
                setIsDataUpdated((prevIsDataUpdated) => !prevIsDataUpdated);
                // reload from the server
                setShouldUpdateDataFromServer((contactItems) => [...contactItems, { operation: "refresh", updated_info: data }]);
                Message.success("Contact deleted");
            },
            (response) => {
                history("/login");
            }, (response) => {
                Message.error("Failed to delete contact");
            }, { "contact_ids": contactIds })

    }

    // Deletes every contact matching the table's filters, only if the match
    // count is still the one the user confirmed (the server refuses otherwise,
    // and refuses a delete with no filter at all).
    const handleRemoveContactsByFilter = () => {
        setSelectedAllModeCommitted(false);
        setSelectAllLocal(false);

        DeleteApi(getAccessTokenSilently,
            "/api/contacts/",
            (data) => {
                setIsDataUpdated((prevIsDataUpdated) => !prevIsDataUpdated);
                setShouldUpdateDataFromServer((contactItems) => [...contactItems, { operation: "refresh", updated_info: data }]);
                Message.success("Deleted " + ((data && data.amount) || 0) + " contact(s)");
            },
            (response) => {
                history("/login");
            }, (err) => {
                Message.error((err && err.message) || "Failed to delete contacts");
            }, { ...matchingPayload(), expected_count: totalCount })
    }

    const handleSelectAllMatchingCommit = (event) => {
        setSelectedAllModeCommitted(!selectedAllModeCommitted);
    }

    const handleContactAddClick = () => {
        setName("");
        setEmail("");
        setContactId("");
        setSubscriptions([]);
        setContactCompanyId("");
        setContactCustomFields({});
        setContactRecordTypeId("");

        setIsAddEditOpen(true);
    }

    const handleContactImportClick = () => {
        setIsImportOpen(true);
    }


    function MyContactImportDialog() {
        return (
            <>
                <ContactImportDialog
                    isOpen={isImportOpen}
                    setIsOpen={setIsImportOpen}
                    edge="end"
                    onSave={(contactRecords) => {
                        handleContactsAdded(contactRecords);
                    }}

                    render={(open) => (
                        <Button
                            // edge="end"
                            type="outline"
                            icon={<IconPlus />}
                            // onClick={open}
                            onClick={() => { handleContactImportClick() }}
                        >
                            Import
                        </Button>
                    )}
                />
            </>
        )
    }

    function MyContactAddEditDialog() {
        return (
            <>
                <ContactAddEditDialog
                    isDataUpdated={isDataUpdated}
                    allMailListItems={allMailListItems}
                    setAllMailListItems={setAllMailListItems}

                    contactName={name}
                    contactEmail={email}
                    contactId={contactId}
                    contactSubscriptions={subscriptions}
                    contactCompanyId={contactCompanyId}
                    contactCustomFields={contactCustomFields}
                    contactRecordTypeId={contactRecordTypeId}

                    isOpen={isAddEditOpen}
                    setIsOpen={setIsAddEditOpen}
                    edge="end"

                    onSave={(contactRecord) => {
                        if (contactRecord.id) {
                            handleContactSaved(contactRecord);
                        } else {
                            handleContactsAdded([contactRecord]);
                        }
                    }}

                    // With no contacts at all, the empty message has its own button.
                    render={(open) => (noContactsYet ? null : (
                        <Button
                            // edge="end"
                            type="primary"
                            icon={<IconPlus />}
                            onClick={() => { handleContactAddClick() }}
                        >
                            Add
                        </Button>
                    ))}

                />
            </>
        )
    }

    function MySelectAllMatching({ onClick, selectedAllModeCommitted, displayedMatchedCount, fullMatchedCount }) {
        return (

            <Checkbox
                checked={selectedAllModeCommitted}
                onChange={() => { setSelectedAllModeCommitted(!selectedAllModeCommitted) }}
                aria-label="select matching contacts on server"
            >
                {"Select All " + fullMatchedCount + " on server"}
            </Checkbox>
        )

    }

    function MyDeleteContactDialog({ selectedAllModeCommitted, totalCount }) {
        return (
            <>
                <DeleteContactDialog
                    count={selectedAllModeCommitted ? totalCount : selected.length}
                    onSave={() => {
                        if (selectedAllModeCommitted) {
                            handleRemoveContactsByFilter()
                        } else {
                            handleRemoveContactsById(selected);
                        }

                        setSelected([]);
                    }}
                    render={(open) => (
                        <Button
                            // className={classes.deleteButton}
                            type="outline"
                            status="danger"
                            icon={<IconDelete />}
                            onClick={open}
                        >
                            {selectedAllModeCommitted || <>Delete {selected.length}</>}
                            {selectedAllModeCommitted && <>Delete {totalCount}</>}

                        </Button>
                    )}
                />
            </>
        )
    }

    useEffect(() => {
        FetchApi(getAccessTokenSilently,
            "/api/contact-lists/",
            (data) => {
                setAllMailListItems(data);
            },
            (response) => {
                history("/login");
            }, (response) => {
                Message.error("Failed to load contact lists");
            })
    }, [history, getAccessTokenSilently, isDataUpdated])

    useEffect(() => {
        FetchApi(getAccessTokenSilently,
            "/api/custom-fields/?entity_type=contact",
            (data) => setCustomFieldDefs(data || []),
            () => {},
            () => {})
    }, [getAccessTokenSilently])

    useEffect(() => {
        FetchApi(getAccessTokenSilently,
            "/api/tags/",
            (data) => setTags(data || []),
            () => history("/login"),
            () => {})
    }, [history, getAccessTokenSilently, isDataUpdated])

    // Deep link (e.g. from Test drive): ?open=<email> goes to that contact's
    // page once it has loaded into the filtered list.
    useEffect(() => {
        if (!openEmail || autoOpened) return;
        const match = contactItems.find((c) => (c.email || "").toLowerCase() === openEmail.toLowerCase());
        if (match) {
            setAutoOpened(true);
            history("/contacts/" + match.id, { replace: true });
        }
    }, [openEmail, contactItems, autoOpened, history])

    return (
        <Content>
            <SimpleBreadcrumbs
                elements={
                    [
                        { name: 'Contacts' },
                    ]
                }

            />

            <div style={{ width: "100%" }}>
                <Row>
                    <Col span={24}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>

                            <MailTextFilter
                                handleSearchFilterChange={handleSearchFilterChange}
                                searchTerm={searchTerm}
                            />

                            <Badge count={termCount(forServer(filter))}>
                                <Button type="outline" icon={<IconFilter />} onClick={() => setFilterDrawerOpen(true)}>
                                    Filters
                                </Button>
                            </Badge>

                            <div style={{ flexGrow: 1 }} />
                            <ColumnPicker
                                visible={visibleColumnKeys}
                                setVisible={setVisibleColumnKeys}
                                customFieldDefs={customFieldDefs}
                            />

                        </div>
                    </Col>
                    {termCount(forServer(filter)) > 0 && (
                        <Col span={24}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                                <FilterChips
                                    filter={forServer(filter)}
                                    options={filterOptions}
                                    onClick={() => setFilterDrawerOpen(true)}
                                    onRemove={(gi, ti) => {
                                        const next = removeTerm(forServer(filter), gi, ti);
                                        applyFilter(termCount(next) ? next : null);
                                    }}
                                />
                                <Button size="mini" type="text" onClick={() => applyFilter(null)}>Clear all</Button>
                            </div>
                        </Col>
                    )}
                    <Col span={24}>
                        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                        {selected.length === 0 && (
                            <>
                                <MyContactImportDialog />
                                <MyContactAddEditDialog />
                                <Button type="outline" icon={<IconRefresh />} onClick={() => handleRefreshList()}>Refresh</Button>
                                <Button type="outline" icon={<IconDownload />} onClick={handleExport}>Export</Button>
                                <Button type="outline" icon={<IconSave />} onClick={() => setSegmentModalVisible(true)}>Save as segment</Button>
                            </>
                        )}

                        {selected.length > 0 && (
                            <>
                                <Dropdown
                                    droplist={
                                        <Menu onClickMenuItem={(key) => key === "__new__" ? setNewTagModalVisible(true) : handleBulkTag(key)}>
                                            {tags.map((t) => (
                                                <Menu.Item key={t.id}>{t.name}</Menu.Item>
                                            ))}
                                            {tags.length > 0 && <Menu.Item key="__divider__" disabled style={{ borderTop: "1px solid var(--color-border-2)" }} />}
                                            <Menu.Item key="__new__">＋ New tag…</Menu.Item>
                                        </Menu>
                                    }
                                    trigger="click"
                                    position="bl"
                                >
                                    <Button type="outline" icon={<IconTag />}>
                                        Tag {bulkCount}
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    droplist={
                                        <Menu onClickMenuItem={(key) => handleBulkAddToList(key)}>
                                            {allMailListItems.map((l) => (
                                                <Menu.Item key={l.id}>{l.name}</Menu.Item>
                                            ))}
                                            {allMailListItems.length === 0 && (
                                                <Menu.Item key="__none__" disabled>No lists yet — create one first</Menu.Item>
                                            )}
                                        </Menu>
                                    }
                                    trigger="click"
                                    position="bl"
                                >
                                    <Button type="outline" icon={<IconList />}>
                                        Add {bulkCount} to list
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    droplist={
                                        <Menu onClickMenuItem={(key) => handleBulkRemoveFromList(key)}>
                                            {allMailListItems.map((l) => (
                                                <Menu.Item key={l.id}>{l.name}</Menu.Item>
                                            ))}
                                            {allMailListItems.length === 0 && (
                                                <Menu.Item key="__none__" disabled>No lists yet</Menu.Item>
                                            )}
                                        </Menu>
                                    }
                                    trigger="click"
                                    position="bl"
                                >
                                    <Button type="outline" icon={<IconMinusCircle />}>
                                        Remove {bulkCount} from list
                                    </Button>
                                </Dropdown>
                                <Button type="outline" status="warning" icon={<IconStop />} onClick={handleBulkUnsubscribe}>
                                    Unsubscribe {bulkCount}
                                </Button>
                                <MyDeleteContactDialog selectedAllModeCommitted={selectedAllModeCommitted} totalCount={totalCount} />
                            </>
                        )}
                        </div>
                    </Col>
                </Row>

                {/* <Toolbar> */}
                    {/* Left side */}


                    {/* Spacer to push buttons to the right */}
                    {/* <div style={{ flexGrow: 1 }} /> */}

                    {/* Right side */}
                    {/* <div style={{ flexGrow: 1 }} /> */}

                {/* </Toolbar> */}

                <SummaryCard
                    title={"Contacts"}
                    subTitle={"Manage you email contacts"}
                    titleElement={
                        selectAllLocal && contactItems.length < totalCount && (
                            <MySelectAllMatching
                                onClick={handleSelectAllMatchingCommit}
                                selectedAllModeCommitted={selectedAllModeCommitted}
                                displayedMatchedCount={contactItems.length}
                                fullMatchedCount={totalCount}
                            />
                        )
                    }
                    topComponent={
                        <InfiniteScrollContactTable
                            contactItems={contactItems}
                            selected={selected}
                            setSelected={setSelected}
                            setSelectAllLocal={setSelectAllLocal}
                            columnDefs={visibleColumns(visibleColumnKeys, customFieldDefs)}
                            sort={sort}
                            onSortChange={handleSortChange}
                            contactListFilterId={contactListFilterId}
                            hasNextPage={hasNextPage}
                            loadItemsLoading={loadItemsLoading}
                            totalCount={totalCount}
                            loadMore={loadMore}
                            loadContactsError={loadContactsError}
                            handleContactRowClick={(row) => history("/contacts/" + row.id)}
                            handleContactAddClick={handleContactAddClick}
                            emptyState={emptyState}
                            onRemoveTag={handleRemoveTagFromContact}
                        />

                    }
                />
            </div>

            <FilterDrawer
                visible={filterDrawerOpen}
                value={filter}
                searchTerm={searchTerm}
                options={filterOptions}
                onClose={() => setFilterDrawerOpen(false)}
                onApply={(next) => { setFilterDrawerOpen(false); applyFilter(next); }}
            />

            <Modal
                title="New tag"
                visible={newTagModalVisible}
                onOk={handleCreateAndTag}
                onCancel={() => setNewTagModalVisible(false)}
                okText="Create & apply"
                simple
            >
                <Input
                    placeholder="Tag name (e.g. VIP, webinar-2026)"
                    value={newTagName}
                    onChange={setNewTagName}
                    onPressEnter={handleCreateAndTag}
                />
            </Modal>

            <Modal
                title="Save as segment"
                visible={segmentModalVisible}
                onOk={handleSaveSegment}
                onCancel={() => setSegmentModalVisible(false)}
                okText="Save segment"
                simple
            >
                <p style={{ color: "var(--color-text-3)", marginTop: 0 }}>
                    Saves the current search and filters as a reusable segment. It stays up to date: anyone who
                    matches later is included.
                </p>
                <Input
                    placeholder="Segment name (e.g. Engaged VIPs)"
                    value={segmentName}
                    onChange={setSegmentName}
                    onPressEnter={handleSaveSegment}
                />
            </Modal>
        </Content>
    );
}
